import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Badge,
  Button,
  Container,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import Select from 'react-select';
import { isValid } from 'iban-ts';
import {
  ActionButtons,
  addStatusToast,
  clearStatusToasts,
  DataTable,
  DataTableItem,
  InputCheck,
  InputNumber,
  InputText,
  Loader,
  PaginationComponent,
  SectionHeader,
  VerticalTabPane,
  VerticalTabs,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccountDetailsFromDb,
  AccountUpdateData,
  AddressResponse,
  AddressUpdateData,
  BankSettingsResponse,
  BankSettingsUpdateData,
  BookingNoteSettingsResponse,
  BookingNoteSettingsUpdateData,
  ContactUpdateData,
  EmailResponse,
  EmailUpdateData,
  getAccountRolesFromDbAsync,
  getAcountDetailsFromDbAsync,
  getAllMaintainerBookingsAsync,
  getAllOwnerBookingsAsync,
  getAllRolesAsync,
  getBankSettingsAsync,
  getBookingNoteSettingsAsync,
  getOfficerSettingsAsync,
  updateOfficerSettingsAsync,
  UpdateOfficerSettingsData,
  getOmaLiittymaSettingsAsync,
  getTarmoSettingsAsync,
  getVatSettingsAsync,
  MaintainerBookingsFilter,
  markAccountDeletedAsyncAsync,
  OfficerResponse,
  OmaLiittymaResponse,
  OmaLiittymaUpdateData,
  OwnerBookingsFilter,
  PhoneResponse,
  PhoneUpdateData,
  selectAccountDetailsFromDb,
  selectAccountDetailsLoadingStatus,
  selectAllRoles,
  selectBankSettings,
  selectbankSettingsLoadingStatus,
  selectBookingNoteData,
  selectMaintainerBookings,
  selectMaintainerBookingsPagination,
  selectMaintainerBookingsStatus,
  selectOfficerSettingsData,
  selectOmaLiittymaData,
  selectOwnerBookings,
  selectOwnerBookingsPagination,
  selectOwnerBookingsStatus,
  selectRolesLoadingStatus,
  selectTarmoSettings,
  selectTarmoSettingsLoadingStatus,
  selectUserRoles,
  selectVatData,
  selectVatLoadingStatus,
  TarmoSettingsResponse,
  TarmoSettingsUpdateData,
  updateAccountDataAsync,
  UpdateBankSettinsAsync,
  UpdateBookingNoteSettinsAsync,
  UpdateOmaLiittymaSettinsAsync,
  UpdateTarmoSettinsAsync,
  UpdateVatSettinsAsync,
  VatSettingsResponse,
  VatSettingsUpdateData,
} from './identityUsersSlice';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import {
  AccountingsQueryFilters,
  getAllPurchaseVatsAsync,
  selectPurchaseVats,
} from '../accountings/accountingSlice';
import {
  AccommodationQueryFilters,
  getAllAccommodationsAsync,
  selectAccommodations,
  selectAccommodationsLoadingStatus,
  selectAccommodationsPagination,
} from '../accommodations/accommodationsSearchSlice';

import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { LOADING_STATUS, LOCALES, PERMISSIONS } from '../../common/constants';
import { checkPermission } from '../auth/authSlice';
import AccountSitesTabContainer from '../sites/AccountSitesTabContainer';
import { TravelAgencyDomain } from '../accommodations/AccommodationDetails';
import {
  BookingType,
  bookingTypeTranslations,
} from '../bookings/Constants/BookingTypeEnum';
import PersonalIdentityTab from './PersonalIdentityTab';

function IdentityUserDetails() {
  const location = useLocation();
  const { from } = location.state || {};
  const params = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchString = searchParams.getAll('searchString');
  const PageNumber = searchParams.getAll('PageNumber');
  const PageSize = searchParams.getAll('PageSize');
  const culture = searchParams.getAll('culture');
  const roleIds = searchParams.getAll('roleIds');
  const isActive = searchParams.getAll('isActive');
  const paysVat = searchParams.getAll('paysVat');
  const createdFrom = searchParams.getAll('createdFrom');
  const createdTo = searchParams.getAll('createdTo');
  const updatedFrom = searchParams.getAll('updatedFrom');
  const updatedTo = searchParams.getAll('updatedTo');
  const companyId = searchParams.getAll('companyId');
  const roleFilter = searchParams.getAll('roleFilter');
  const backLinkUrl =
    from ||
    `/users/search${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const TabStatus = searchParams.get('Tabs');
  const dispatch = useAppDispatch();
  const allRoles = useAppSelector(selectAllRoles);
  const userRoles = useAppSelector(selectUserRoles);
  const rolesStatus = useAppSelector(selectRolesLoadingStatus);
  const countries = useAppSelector(selectCountryItems);
  const accountDetails = useAppSelector(selectAccountDetailsFromDb);
  const omaLiittymaSettings = useAppSelector(selectOmaLiittymaData);
  const bankSettings = useAppSelector(selectBankSettings);
  const tarmoSettings = useAppSelector(selectTarmoSettings);
  const ownerBookings = useAppSelector(selectOwnerBookings);
  const ownerBookingsLoadingStatus = useAppSelector(selectOwnerBookingsStatus);
  const maintainerBookings = useAppSelector(selectMaintainerBookings);
  const maintainerBookingsLoadingStatus = useAppSelector(
    selectMaintainerBookingsStatus,
  );
  const bankSettingsLoadingStatus = useAppSelector(
    selectbankSettingsLoadingStatus,
  );
  const tarmoSettingsLoadingStatus = useAppSelector(
    selectTarmoSettingsLoadingStatus,
  );
  const officerSettings = useAppSelector(selectOfficerSettingsData);
  const hasSettingModify = useAppSelector(
    checkPermission([PERMISSIONS.SettingModify]),
  );
  const bookingNoteSettings = useAppSelector(selectBookingNoteData);
  const vatSettings = useAppSelector(selectVatData);
  const vatSettingsLoadingStatus = useAppSelector(selectVatLoadingStatus);
  const accountLoadingStatus = useAppSelector(
    selectAccountDetailsLoadingStatus,
  );

  const accommodations = useAppSelector(selectAccommodations);

  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );
  const accommodationsPagination = useAppSelector(
    selectAccommodationsPagination,
  );

  const ownerBookingsPagination = useAppSelector(selectOwnerBookingsPagination);
  const maintainerBookingsPagination = useAppSelector(
    selectMaintainerBookingsPagination,
  );
  const [isAddressModalVisible, setIsAddressModalVisible] =
    useState<boolean>(false);
  const [isPhoneModalVisible, setIsPhoneModalVisible] =
    useState<boolean>(false);
  const [isEMailModalVisible, setIsEMailModalVisible] =
    useState<boolean>(false);

  const [ownerBookingsFilter, setOwnerBookingsFilter] =
    useState<OwnerBookingsFilter>({
      ownerId: params.identityId,
      PageNumber: 1,
      PageSize: 30,
    } as OwnerBookingsFilter);

  const [maintainerBookingsFilter, setMaintainerBookingsFilter] =
    useState<MaintainerBookingsFilter>({
      maintainerId: params.identityId,
      PageNumber: 1,
      PageSize: 30,
    } as MaintainerBookingsFilter);

  const purchaseVats = useAppSelector(selectPurchaseVats);
  const travelAgencyDomains: TravelAgencyDomain[] = [
    {
      id: '01089d85-d32f-4136-9d20-1a030a37990e',
      partnerName: 'Saariselkä',
    },
  ];
  const travelAgencyDomainsList = travelAgencyDomains.map(partner => {
    return { value: partner.id, label: partner.partnerName };
  });

  const purchaseVatsList = purchaseVats.map(pvat => {
    return { value: pvat.id, label: pvat.name };
  });

  const [accommodationTableItems, setAccommodationTableItems] = useState<
    DataTableItem[]
  >([]);

  function getIsOwner(): boolean {
    const filteredRoles =
      allRoles && allRoles.rolesList && userRoles
        ? allRoles.rolesList.filter(
            ar =>
              userRoles.includes(ar.id) &&
              ar.name.includes('Owner') &&
              !ar.name.includes('Application'),
          )
        : null;
    return filteredRoles != null && filteredRoles.length > 0;
  }
  function getIsCaretaker(): boolean {
    const filteredRoles =
      allRoles && allRoles.rolesList && userRoles
        ? allRoles.rolesList.filter(
            ar =>
              userRoles.includes(ar.id) &&
              (ar.name.includes('Cleaner') || ar.name.includes('Maintainer')) &&
              !ar.name.includes('Application'),
          )
        : null;
    return filteredRoles != null && filteredRoles.length > 0;
  }
  function getIsOfficer() {
    const filteredRoles =
      allRoles && allRoles.rolesList && userRoles
        ? allRoles.rolesList.filter(
            ar =>
              userRoles.includes(ar.id) && ar.name.includes('Administrator'),
          )
        : null;
    return filteredRoles != null && filteredRoles.length > 0;
  }

  const Tabs: string[] = [
    'Perustiedot',
    'Yhteystiedot',
    'Roolit',
    'Varaamot',
    'Omaliittymä ja Rooli asetukset',
    'Varaustiedotteiden lähetys asetukset',
    'Pankkiyhteys',
    'Virkailijan asetukset ja tiedot',
    'Alv asetukset',
    'Kohteet',
    'Tarmo asetukset',
    'Varaukset',
    'Henkilötiedot',
  ];

  // const Tabs: string[] = [
  //   'Perustiedot',
  //   'Yhteystiedot',
  //   'Roolit',
  //   'Omaliittymä ja Rooli asetukset',
  //   'Varaustiedotteiden lähetys asetukset',
  //   'Pankkiyhteys',
  //   'Virkailijan asetukset ja tiedot',
  //   'Alv asetukset',
  //   'Kohteet',
  //   'Tarmo asetukset',
  //   'Varaukset',
  // ];
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);
  const [editedOmaLiittymaSettings, setEditedOmaLiittymaSettings] =
    useState<OmaLiittymaResponse>({
      id: '',
      caretakerNumber: 0,
      directLinkPath: '',
      hideBillOwnerData: false,
      ownerNumber: 0,
      accountId: '',
      lastRequestDate: '',
    } as OmaLiittymaResponse);
  const [editedBookingNoteSettings, setEditedBookingNoteSettings] =
    useState<BookingNoteSettingsResponse>({
      id: '',
      accountId: '',
      bookingNoteByEmail: false,
      bookingNoteByMail: false,
      bookingNoteBySms: false,
      bookingNoteImmediatelly: false,
      bookingNoteWeekly: false,
      noMassSms: false,
      noBookingNote: false,
    } as BookingNoteSettingsResponse);

  const [editedVatSettings, setEditedVatSettings] =
    useState<VatSettingsResponse>({
      id: '',
      accountId: '',
      hetu: '',
      otherVatCode: '',
      paysVat: false,
      yCode: '',
    } as VatSettingsResponse);

  const [editedTarmoSettings, setEditedTarmoSettings] =
    useState<TarmoSettingsResponse>({
      id: '',
      tarmoAPIAddress: '',
      tarmoAPIKey: '',
    } as TarmoSettingsResponse);

  const [editedBankSettings, setEditedBankSettings] =
    useState<BankSettingsResponse>({
      id: '',
      accountId: '',
      bic: '',
      iban: '',
    } as BankSettingsResponse);

  const [editedOfficerSettings, setEditedOfficerSettings] =
    useState<OfficerResponse>({
      id: '',
      officerNumber: '',
      accountId: '',
      agentNumber: null,
    } as OfficerResponse);

  const [editedEmail, setEditedEmail] = useState<EmailResponse>({
    id: '',
    description: '',
    emailAddress: '',
    isPrimary: false,
    isBookingNoteEmail: false,
  } as EmailResponse);

  const [editedPhone, setEditedPhone] = useState<PhoneResponse>({
    id: '',
    typeId: '',
    phoneNumber: '',
    description: '',
    isPrimary: false,
    bookingNoteBySms: false,
  } as PhoneResponse);

  const [editedAddress, setEditedAddress] = useState<AddressResponse>({
    id: null,
    postStreetAddress: '',
    postCity: '',
    postCode: '',
    isPrimary: false,
    description: '',
    additionalAddressLine: '',
    countryId: '',
  } as AddressResponse);

  const [editedRoles, setEditedRoles] = useState<string[]>([]);

  const [editedTravelAgencyDomains, setEditedTravelAgencyDomains] = useState<
    string[]
  >([]);

  const [editedAccount, setEditedAccount] = useState<AccountDetailsFromDb>({
    id: '',
    addresses: [
      {
        id: '',
        postStreetAddress: '',
        postCity: '',
        postCode: '',
        isPrimary: false,
        description: '',
        additionalAddressLine: '',
        countryId: '',
      },
    ] as AddressResponse[],
    companyName: '',
    additionalInformation: '',
    description: '',
    emails: [
      {
        id: '',
        description: '',
        emailAddress: '',
        isPrimary: false,
        isBookingNoteEmail: false,
      } as EmailResponse,
    ],
    firstName: '',
    lastName: '',
    phones: [
      {
        id: '',
        typeId: '',
        phoneNumber: '',
        description: '',
        isPrimary: false,
        bookingNoteBySms: false,
      } as PhoneResponse,
    ],
    www: '',
    subjectId: '',
  } as AccountDetailsFromDb);

  function resetData() {
    setEditedOmaLiittymaSettings({
      id: '',
      caretakerNumber: 0,
      directLinkPath: '',
      hideBillOwnerData: false,
      ownerNumber: 0,
      accountId: '',
      lastRequestDate: '',
    } as OmaLiittymaResponse);
    setEditedOfficerSettings({
      id: '',
      officerNumber: '',
      accountId: '',
      agentNumber: null,
    } as OfficerResponse);
    setEditedAccount({
      id: '',
      addresses: [
        {
          id: '',
          postStreetAddress: '',
          postCity: '',
          postCode: '',
          isPrimary: false,
          description: '',
          additionalAddressLine: '',
          countryId: '',
        },
      ] as AddressResponse[],
      companyName: '',
      additionalInformation: '',
      description: '',
      emails: [
        {
          id: '',
          description: '',
          emailAddress: '',
          isPrimary: false,
          isBookingNoteEmail: false,
        } as EmailResponse,
      ],
      firstName: '',
      lastName: '',
      phones: [
        {
          id: '',
          typeId: '',
          phoneNumber: '',
          description: '',
          isPrimary: false,
          bookingNoteBySms: false,
        } as PhoneResponse,
      ],
      www: '',
      subjectId: '',
    } as AccountDetailsFromDb);
    setEditedPhone({
      id: '',
      typeId: '',
      phoneNumber: '',
      description: '',
      isPrimary: false,
      bookingNoteBySms: false,
    } as PhoneResponse);
    setEditedAddress({
      id: null,
      postStreetAddress: '',
      postCity: '',
      postCode: '',
      isPrimary: false,
      description: '',
      additionalAddressLine: '',
      countryId: '',
    } as AddressResponse);
    setEditedBankSettings({
      id: '',
      accountId: '',
      bic: '',
      iban: '',
    } as BankSettingsResponse);
    setEditedBookingNoteSettings({
      id: '',
      accountId: '',
      bookingNoteByEmail: false,
      bookingNoteByMail: false,
      bookingNoteBySms: false,
      bookingNoteImmediatelly: false,
      bookingNoteWeekly: false,
      noMassSms: false,
      noBookingNote: false,
    } as BookingNoteSettingsResponse);
    setEditedEmail({
      id: '',
      description: '',
      emailAddress: '',
      isPrimary: false,
      isBookingNoteEmail: false,
    } as EmailResponse);
    setEditedRoles([]);
    setEditedVatSettings({
      id: '',
      accountId: '',
      hetu: '',
      otherVatCode: '',
      paysVat: false,
      yCode: '',
    } as VatSettingsResponse);
  }
  const initialAccountingFilter: AccountingsQueryFilters = {
    pageNumber: 1,
    pageSize: 100,
    culture: 'fi_FI',
  };

  const [accountingFilter, setAccountingFilter] = useState(
    initialAccountingFilter,
  );
  const [filtering, setFiltering] = useState<AccommodationQueryFilters>({
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    filterCriteria: {
      showDeleted: false,
      searchString: undefined,
      qualityRatings: [],
      accommodationNumber: undefined,
      novelty: undefined,
      onSale: undefined,
      showInOwnerPage: undefined,
      accommodationGroupId: undefined,
      accountId: params.identityId,
    },
  });
  const history = useNavigate();
  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = filtering;
    paginatedFiltering.PageNumber = Page;
    setFiltering(paginatedFiltering);
    dispatch(getAllAccommodationsAsync(paginatedFiltering));
  };
  const initFetch = useCallback(() => {
    if (TabStatus != null) {
      setActiveTab(Tabs[11]);
    }
    resetData();
    dispatch(getAcountDetailsFromDbAsync(params.identityId));
    dispatch(getAccountRolesFromDbAsync(params.identityId));
    dispatch(getAllAccommodationsAsync(filtering));
    dispatch(getAllPurchaseVatsAsync(accountingFilter));
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
        culture: LOCALES.Fi,
      }),
    );
    dispatch(
      getAllRolesAsync({
        pageNumber: 1,
        pageSize: 10,
        culture: 'fi_FI',
        searchString: '',
      }),
    );
    if (getIsOwner()) {
      dispatch(getAllOwnerBookingsAsync(ownerBookingsFilter));
    }
    if (getIsCaretaker()) {
      dispatch(getAllMaintainerBookingsAsync(maintainerBookingsFilter));
    }
  }, [dispatch, params.identityId]);
  useEffect(() => {
    if (accommodations && accommodations.length > 0) {
      const formattedAccommodationList: DataTableItem[] = [];
      accommodations?.forEach(item => {
        formattedAccommodationList.push({
          ItemId: item.id,
          Items: [item.accommodationNumber.toString(), item.name],
          Url: `/accommodations/details/${item.id}`,
        });
      });
      setAccommodationTableItems(formattedAccommodationList);
    }
  }, [accommodations]);
  useEffect(() => {
    if (editedAccount.travelAgencyDomains) {
      setEditedTravelAgencyDomains(editedAccount.travelAgencyDomains);
    }
  }, [editedAccount.travelAgencyDomains]);
  function isSelected(itemId) {
    if (editedRoles != null) {
      return editedRoles.some(s =>
        s.toUpperCase().includes(itemId.toUpperCase()),
      );
    }
    return false;
  }
  const travelAgencyDomainSelectionVisible = allRoles?.rolesList.some(
    item =>
      (item.id.toUpperCase() === '61BE6E51-2241-4674-BF7E-ABBAC8BBA433' ||
        item.id.toUpperCase() === '11E946C0-2B4E-493A-AADA-C01DD3E98822') &&
      isSelected(item.id.toUpperCase()),
  );
  useEffect(() => {
    if (
      !travelAgencyDomainSelectionVisible &&
      editedTravelAgencyDomains.length > 0
    ) {
      setEditedTravelAgencyDomains([]);
    }
  }, [travelAgencyDomainSelectionVisible]);
  useEffect(() => {
    initFetch();
  }, [initFetch, params.identityId]);
  useEffect(() => {
    // Reset local state when component mounts
    setEditedVatSettings({
      id: '',
      accountId: '',
      hetu: '',
      otherVatCode: '',
      paysVat: false,
      yCode: '',
      purchaseVatId: '',
    });
    setEditedBankSettings({
      id: '',
      accountId: '',
      bic: '',
      iban: '',
    });
    // Reset other edited states as needed
  }, []);
  useEffect(() => {
    if (getIsOwner()) {
      dispatch(getAllOwnerBookingsAsync(ownerBookingsFilter));
    }
    if (getIsCaretaker()) {
      dispatch(getAllMaintainerBookingsAsync(maintainerBookingsFilter));
    }
  }, [allRoles]);

  useEffect(() => {
    if (accountDetails != null) {
      setEditedAccount(accountDetails);
    }
  }, [accountDetails]);
  useEffect(() => {
    if (
      bankSettings != null &&
      bankSettings.accountId === params.identityId.toUpperCase()
    ) {
      setEditedBankSettings(bankSettings);
    }
  }, [bankSettingsLoadingStatus]);

  useEffect(() => {
    if (
      tarmoSettings != null &&
      tarmoSettings.accountId === params.identityId.toUpperCase()
    ) {
      setEditedTarmoSettings(tarmoSettings);
    }
  }, [tarmoSettingsLoadingStatus]);

  useEffect(() => {
    if (userRoles != null) {
      setEditedRoles(userRoles);
    } else {
      setEditedRoles([]);
    }
    if (
      getIsOwner() &&
      accountLoadingStatus === LOADING_STATUS.IDLE &&
      accountDetails != null
    ) {
      dispatch(getOmaLiittymaSettingsAsync(params.identityId));
      dispatch(getBookingNoteSettingsAsync(params.identityId));
      dispatch(getBankSettingsAsync(params.identityId));
      dispatch(getVatSettingsAsync(params.identityId));
    }
    if (
      getIsCaretaker() &&
      accountLoadingStatus === LOADING_STATUS.IDLE &&
      accountDetails != null
    ) {
      // console.log('useEffect, fetching TARMO data');
      dispatch(getOmaLiittymaSettingsAsync(params.identityId));
      dispatch(getBookingNoteSettingsAsync(params.identityId));
      dispatch(getBankSettingsAsync(params.identityId));
      dispatch(getVatSettingsAsync(params.identityId));
      dispatch(getTarmoSettingsAsync(params.identityId));
    }
    if (
      getIsOfficer() &&
      accountLoadingStatus === LOADING_STATUS.IDLE &&
      accountDetails != null
    ) {
      dispatch(getOfficerSettingsAsync(params.identityId));
    } else {
      setEditedOfficerSettings({
        id: '',
        officerNumber: '',
        accountId: '',
      } as OfficerResponse);
    }
  }, [userRoles, editedAccount, allRoles]);
  useEffect(() => {
    if (editedRoles != null) {
      setEditedRoles(editedRoles);
      setEditedVatSettings(vatSettings);
      setEditedBankSettings(bankSettings);
    }
  }, [editedRoles, vatSettings, ownerBookings, maintainerBookings]);

  useEffect(() => {
    if ((getIsOwner() || getIsCaretaker()) && omaLiittymaSettings != null) {
      setEditedOmaLiittymaSettings(omaLiittymaSettings);
    } else {
      setEditedOmaLiittymaSettings({
        id: '',
        caretakerNumber: 0,
        directLinkPath: '',
        hideBillOwnerData: false,
        ownerNumber: 0,
        accountId: '',
        lastRequestDate: '',
      } as OmaLiittymaResponse);
    }
  }, [omaLiittymaSettings]);
  useEffect(() => {}, [officerSettings]);

  useEffect(() => {
    if (bankSettings != null && bankSettings.accountId === editedAccount.id) {
      setEditedBankSettings(bankSettings);
    }
  }, [editedAccount.id, bankSettings]);

  useEffect(() => {
    if (tarmoSettings != null && tarmoSettings.accountId === tarmoSettings.id) {
      setEditedTarmoSettings(tarmoSettings);
    }
  }, [editedAccount.id, tarmoSettings]);

  useEffect(() => {
    if (getIsOfficer() && officerSettings != null) {
      setEditedOfficerSettings(officerSettings);
    } else {
      setEditedOfficerSettings({
        id: '',
        officerNumber: '',
        accountId: '',
      } as OfficerResponse);
    }
  }, [officerSettings]);

  useEffect(() => {
    if (getIsOwner() || getIsCaretaker()) {
      if (bookingNoteSettings != null) {
        setEditedBookingNoteSettings(bookingNoteSettings);
      } else {
        setEditedBookingNoteSettings({
          id: '',
          accountId: '',
          bookingNoteByEmail: false,
          bookingNoteByMail: false,
          bookingNoteBySms: false,
          bookingNoteImmediatelly: false,
          bookingNoteWeekly: false,
          noMassSms: false,
          noBookingNote: false,
        } as BookingNoteSettingsResponse);
      }
    } else {
      setEditedBookingNoteSettings(null);
    }
  }, [bookingNoteSettings]);
  useEffect(() => {
    if (getIsOwner() || getIsCaretaker()) {
      if (vatSettings != null) {
        setEditedVatSettings(vatSettings);
      } else {
        setEditedVatSettings({
          id: '',
          accountId: '',
          hetu: '',
          otherVatCode: '',
          paysVat: false,
          yCode: '',
        } as VatSettingsResponse);
      }
    } else {
      setEditedVatSettings(null);
    }
  }, [vatSettings]);
  useEffect(() => {}, [ownerBookings]);
  useEffect(() => {}, [ownerBookingsLoadingStatus]);

  function getOmaLiittymaUpdateData() {
    return {
      id: editedOmaLiittymaSettings.id,
      accountId: editedAccount.id,
      caretakerNumber: editedOmaLiittymaSettings.caretakerNumber,
      directLinkPath: editedOmaLiittymaSettings.directLinkPath,
      hideBillOwnerData: editedOmaLiittymaSettings.hideBillOwnerData,
      ownerNumber: editedOmaLiittymaSettings.ownerNumber,
    } as OmaLiittymaUpdateData;
  }
  function getBookingNoteUpdateData() {
    return {
      id: editedBookingNoteSettings.id,
      bookingNoteByEmail: editedBookingNoteSettings.bookingNoteByEmail,
      bookingNoteByMail: editedBookingNoteSettings.bookingNoteByMail,
      bookingNoteBySms: editedBookingNoteSettings.bookingNoteBySms,
      bookingNoteImmediatelly:
        editedBookingNoteSettings.bookingNoteImmediatelly,
      bookingNoteWeekly: editedBookingNoteSettings.bookingNoteWeekly,
      noBookingNote: editedBookingNoteSettings.noBookingNote,
      accountId: editedAccount.id,
      noMassSms: editedBookingNoteSettings.noMassSms,
    } as BookingNoteSettingsUpdateData;
  }

  function getVatUpdateData() {
    const vatUpdateData = {
      id: editedVatSettings.id,
      hetu: editedVatSettings.hetu,
      accountId: editedAccount.id,
      otherVatCode: editedVatSettings.otherVatCode,
      paysVat: editedVatSettings.paysVat,
      yCode: editedVatSettings.yCode,
      purchaseVatId: editedVatSettings.purchaseVatId,
    } as VatSettingsUpdateData;

    return vatUpdateData;
  }

  function getTarmoUpdateData() {
    /*
    let caretakerId = editedTarmoSettings.id;
    if (
      caretakerId === undefined ||
      caretakerId === null ||
      caretakerId === ''
    ) {
      caretakerId = params.identityId;
    }
     */
    const tarmoUpdateData = {
      id: editedTarmoSettings.id,
      tarmoAPIAddress: editedTarmoSettings.tarmoAPIAddress,
      tarmoAPIKey: editedTarmoSettings.tarmoAPIKey,
      accountId: editedAccount.id,
    } as TarmoSettingsUpdateData;

    return tarmoUpdateData;
  }

  function getBankSettingsUpdateData() {
    return {
      id: editedBankSettings.id,
      bic: editedBankSettings.bic,
      iban: editedBankSettings.iban,
      accountId: editedAccount.id,
    } as BankSettingsUpdateData;
  }
  function getUpdateData() {
    let accountUpdateData: AccountUpdateData;
    accountUpdateData = {
      id: null,
      contactUpdateData: null,
      accountsRoles: null,
      travelAgencyDomains: null,
    } as AccountUpdateData;

    if (editedAccount != null) {
      accountUpdateData = { ...accountUpdateData, id: editedAccount.id };
    }
    if (
      editedAccount != null &&
      (editedAccount.firstName != null ||
        editedAccount.lastName != null ||
        editedAccount.www != null ||
        editedAccount.companyName != null ||
        editedAccount.description != null ||
        editedAccount.additionalInformation != null)
    ) {
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: {
          firstName: editedAccount.firstName,
          companyName: editedAccount.companyName,
          additionalInformation: editedAccount.additionalInformation,
          addresses: null,
          description: editedAccount.description,
          emails: null,
          lastName: editedAccount.lastName,
          www: editedAccount.www,
          phones: null,
        },
      };
    } else {
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: {
          firstName: '',
          companyName: '',
          additionalInformation: '',
          addresses: null,
          description: '',
          emails: null,
          lastName: '',
          www: '',
          phones: null,
        } as ContactUpdateData,
      };
    }

    if (editedAccount != null && editedAccount.emails != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        emails: editedAccount.emails.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            isBookingNoteEmail: data.isBookingNoteEmail,
            description: data.description,
            emailAddress: data.emailAddress,
          } as EmailUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }

    if (editedAccount != null && editedAccount.phones != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        phones: editedAccount.phones.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            bookingNoteBySms: data.bookingNoteBySms,
            description: data.description,
            phoneNumber: data.phoneNumber,
            typeId: data.typeId,
          } as PhoneUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }

    if (editedAccount != null && editedAccount.addresses != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        addresses: editedAccount.addresses.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            description: data.description,
            additionalAddressLine: data.additionalAddressLine,
            countryId: data.countryId === '' ? null : data.countryId,
            postCode: data.postCode,
            postCity: data.postCity,
            postStreetAddress: data.postStreetAddress,
          } as AddressUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }
    /*
    if (editedAccount != null && editedAccount.travelAgencyDomains != null) {
      accountUpdateData = {
        ...accountUpdateData,
        travelAgencyDomains: editedAccount.travelAgencyDomains,
      };
    }
    */
    if (editedTravelAgencyDomains != null) {
      accountUpdateData = {
        ...accountUpdateData,
        travelAgencyDomains:
          editedTravelAgencyDomains.length > 0
            ? editedTravelAgencyDomains
            : null,
      };
    }
    if (userRoles != null) {
      const temproles = [...editedRoles];
      const updatedRoles = temproles.map(tr => tr);
      accountUpdateData = { ...accountUpdateData, accountsRoles: updatedRoles };
    }
    return accountUpdateData;
  }
  function addRemoveSelectedRole(event, roleId) {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value === true) {
      if (
        editedRoles.some(s =>
          s.toUpperCase().includes(roleId.toUpperCase()),
        ) === true
      ) {
        return;
      }

      const tempuserRoles = [...editedRoles, roleId.toUpperCase().toString()];
      setEditedRoles(tempuserRoles);
    } else if (
      editedRoles.some(s => s.toUpperCase().includes(roleId.toUpperCase()))
    ) {
      const itemIndex = editedRoles.findIndex(o =>
        o.toUpperCase().includes(roleId.toUpperCase()),
      );
      if (itemIndex > -1) {
        const tempUserRoles = [...editedRoles];
        tempUserRoles.splice(itemIndex, 1);
        setEditedRoles(tempUserRoles);
      }
    }
  }
  const handleTravelAgencyDiscountPercentChange = () => {};
  /*
  const handleTravelAgencyDomainsChange = selectedOptions => {
    // editedTravelAgencyDomains
    const storeAccount = { ...editedAccount };
    if (selectedOptions == null || selectedOptions.length === 0) {
      storeAccount.travelAgencyDomains = null;
    } else {
      storeAccount.travelAgencyDomains = selectedOptions.map(
        option => option.value,
      );
    }
    // setEditedAccount(storeAccount);
  };
   */
  const handleTravelAgencyDomainsChange = selectedOptions => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setEditedTravelAgencyDomains([]);
    } else {
      setEditedTravelAgencyDomains(selectedOptions.map(opt => opt.value));
    }
  };

  const rolesSelectOptions = allRoles?.rolesList.map(item => (
    <Form.Group className="mb-24" key={item.id}>
      <Form.Check
        className="mb-24"
        label={item.description ?? item.name}
        name={item.name}
        checked={isSelected(item.id.toUpperCase())}
        onChange={event => addRemoveSelectedRole(event, item.id)}
      />
      {/* TODO: If travelagency role selected, show field for discount percent
      {item.id.toUpperCase() === '61BE6E51-2241-4674-BF7E-ABBAC8BBA433' && (
        <InputText
          HandleChange={handleTravelAgencyDiscountPercentChange}
          Id="travelAgencyDiscountPercent"
          Name="travelAgencyDiscountPercent"
          Value={editedAccount.firstName ?? ''}
          IsRequired={false}
          Label="Matkatoimisto käyttäjän alennus prosentti"
        />
      )}
      */}
    </Form.Group>
  ));

  const handleSelectChangeAddress = selectedOption => {
    if (
      editedAccount.addresses != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.addresses.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedAddress(data[0]);
      }
    }
  };

  const handleSelectChangePhone = selectedOption => {
    if (
      editedAccount.phones != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.phones.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedPhone(data[0]);
      }
    }
  };
  const handleSelectChangeEmail = selectedOption => {
    if (
      editedAccount.emails != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.emails.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedEmail(data[0]);
      }
    }
  };
  const handleEditAddressClick = selectedOption => {
    handleSelectChangeAddress(selectedOption);
    setIsAddressModalVisible(true);
  };
  const handleEditBookingClick = selectedOption => {
    window.open(`/bookings/${selectedOption.id}`, '_blank');
  };
  const handleEditPhoneClick = selectedOption => {
    handleSelectChangePhone(selectedOption);
    setIsPhoneModalVisible(true);
  };

  const handleEditEmailClick = selectedOption => {
    handleSelectChangeEmail(selectedOption);
    setIsEMailModalVisible(true);
  };

  function removeAddress(removedItem) {
    if (editedAccount != null && editedAccount.addresses != null) {
      const tempaddresses = editedAccount.addresses.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        addresses: tempaddresses,
      };
      setEditedAccount(storedAccount);
    }
  }

  function removePhone(removedItem) {
    if (editedAccount != null && editedAccount.phones != null) {
      const tempPhones = editedAccount.phones.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        phones: tempPhones,
      };
      setEditedAccount(storedAccount);
    }
  }

  function removeEmail(removedItem) {
    if (editedAccount != null && editedAccount.emails != null) {
      const tempEmail = editedAccount.emails.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        emails: tempEmail,
      };
      setEditedAccount(storedAccount);
    }
  }
  function getBookingTypeTranslation(
    bookingType: BookingType,
    locale: 'en' | 'fi',
  ): string {
    return bookingTypeTranslations[locale][bookingType] || 'Unknown';
  }
  const handlePaginationClickOwners = (Page: number) => {
    const paginatedFiltering = ownerBookingsFilter;
    paginatedFiltering.PageNumber = Page;
    setOwnerBookingsFilter(paginatedFiltering);
    dispatch(getAllOwnerBookingsAsync(paginatedFiltering));
  };

  const handlePaginationClickMaintainers = (Page: number) => {
    const paginatedFiltering = maintainerBookingsFilter;
    paginatedFiltering.PageNumber = Page;
    setMaintainerBookingsFilter(paginatedFiltering);
    dispatch(getAllMaintainerBookingsAsync(paginatedFiltering));
  };

  const ownerBookingsListItems = useMemo(() => {
    return ownerBookingsLoadingStatus === LOADING_STATUS.IDLE ? (
      ownerBookings?.map(item => (
        <>
          <tr key={item.id}>
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.bookingNumber}
              </Link>
            </td>
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab1' }}
              >
                {new Date(item.startDate).toLocaleDateString('fi-FI')}
                {' - '}
                {new Date(item.endDate).toLocaleDateString('fi-FI')}
              </Link>
            </td>
            {/* <td> */}
            {/*  <Link */}
            {/*    className="d-block" */}
            {/*    to={`/bookings/${item.id}`} */}
            {/*    state={{ backUrl: '/?tab=tab2' }} */}
            {/*  > */}
            {/*    {getBookingTypeTranslation(item., 'fi')} */}
            {/*  </Link> */}
            {/* </td> */}
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.payerFirstName} {item.payerLastName}
              </Link>
            </td>
            <td>
              <Link
                className="nav-link"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.itemName}
              </Link>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <Link
                      className="d-block"
                      to={`/bookings/${item.id}`}
                      state={{ backUrl: '/?tab=tab1' }}
                    >
                      <Badge bg="danger">Peruttu</Badge>
                    </Link>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <Link
                      className="d-block"
                      to={`/bookings/${item.id}`}
                      state={{ backUrl: '/?tab=tab2' }}
                    >
                      <Badge bg="primary">Vahvistettu</Badge>
                    </Link>
                  );
                }
                return badge;
              })()}
            </td>
            <td>
              {item.price}
              {' €'}
            </td>
            <td>
              {item.purchasePrice}
              {' €'}
            </td>
          </tr>
          {item.additionalOrderItems &&
            item.additionalOrderItems.map((additionalItem, index) => (
              <tr key={additionalItem.id}>
                <td />
                <td>{additionalItem.itemName}</td>
                <td />
                <td />
                <td />
                <td>
                  {additionalItem.price}
                  {' €'}
                </td>
                <td>
                  {additionalItem.purchasePrice}
                  {' €'}
                </td>
              </tr>
            ))}
        </>
      ))
    ) : (
      <div />
    );
  }, [ownerBookings, ownerBookingsLoadingStatus]);

  // const ownerBookingsListItems = useMemo(() => {
  //   return ownerBookingsLoadingStatus === LOADING_STATUS.IDLE ? (
  //     ownerBookings?.map(item => (
  //       <tr key={item.id}>
  //         <td style={{ width: '30%' }}>
  //           <Button
  //             variant="link"
  //             size="sm"
  //             onClick={() => handleEditBookingClick(item)}
  //           >
  //             {item.bookingNumber ?? ''}
  //           </Button>
  //         </td>
  //         <td style={{ width: '30%' }}>
  //           <Button
  //             variant="link"
  //             size="sm"
  //             onClick={() => handleEditBookingClick(item)}
  //           >
  //             {new Date(item.startDate).toLocaleDateString('fi-FI')}
  //             {' - '}
  //             {new Date(item.endDate).toLocaleDateString('fi-FI')}
  //           </Button>
  //         </td>
  //         <td style={{ width: 100 }}>
  //           <Button
  //             variant="outline-blue"
  //             size="sm"
  //             onClick={() => handleEditBookingClick(item)}
  //           >
  //             {getBookingTypeTranslation(item.bookingType, 'fi')}
  //           </Button>
  //         </td>
  //       </tr>
  //     ))
  //   ) : (
  //     <div />
  //   );
  // }, [ownerBookings, ownerBookingsLoadingStatus]);

  const maintainerBookingsListItems = useMemo(() => {
    return maintainerBookingsLoadingStatus === LOADING_STATUS.IDLE ? (
      maintainerBookings?.map(item => (
        <>
          <tr key={item.id}>
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.bookingNumber}
              </Link>
            </td>
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab1' }}
              >
                {new Date(item.startDate).toLocaleDateString('fi-FI')}
                {' - '}
                {new Date(item.endDate).toLocaleDateString('fi-FI')}
              </Link>
            </td>
            {/* <td> */}
            {/*  <Link */}
            {/*    className="d-block" */}
            {/*    to={`/bookings/${item.id}`} */}
            {/*    state={{ backUrl: '/?tab=tab2' }} */}
            {/*  > */}
            {/*    {getBookingTypeTranslation(item., 'fi')} */}
            {/*  </Link> */}
            {/* </td> */}
            <td>
              <Link
                className="d-block"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.payerFirstName} {item.payerLastName}
              </Link>
            </td>
            <td>
              <Link
                className="nav-link"
                to={`/bookings/${item.id}`}
                state={{ backUrl: '/?tab=tab2' }}
              >
                {item.itemName}
              </Link>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <Link
                      className="d-block"
                      to={`/bookings/${item.id}`}
                      state={{ backUrl: '/?tab=tab1' }}
                    >
                      <Badge bg="danger">Peruttu</Badge>
                    </Link>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <Link
                      className="d-block"
                      to={`/bookings/${item.id}`}
                      state={{ backUrl: '/?tab=tab2' }}
                    >
                      <Badge bg="primary">Vahvistettu</Badge>
                    </Link>
                  );
                }
                return badge;
              })()}
            </td>
            <td>
              {item.price}
              {' €'}
            </td>
            <td>
              {item.purchasePrice}
              {' €'}
            </td>
          </tr>
          {item.additionalOrderItems &&
            item.additionalOrderItems.map((additionalItem, index) => (
              <tr key={additionalItem.id}>
                <td />
                <td>{additionalItem.itemName}</td>
                <td />
                <td />
                <td />
                <td>
                  {additionalItem.price}
                  {' €'}
                </td>
                <td>
                  {additionalItem.purchasePrice}
                  {' €'}
                </td>
              </tr>
            ))}
        </>
      ))
    ) : (
      <div />
    );
  }, [maintainerBookings, maintainerBookingsLoadingStatus]);

  // const maintainerBookingsListItems = useMemo(() => {
  //   return maintainerBookingsLoadingStatus === LOADING_STATUS.IDLE ? (
  //     maintainerBookings?.map(item => (
  //       <tr key={item.id}>
  //         <td style={{ width: '30%' }}>{item.bookingNumber ?? ''}</td>
  //         <td style={{ width: '100%' }}>
  //           {item.bookingType != null
  //             ? getBookingTypeTranslation(item.bookingType, 'fi')
  //             : ''}
  //         </td>
  //         <td style={{ width: 100 }}>
  //           <Button
  //             variant="outline-blue"
  //             size="sm"
  //             onClick={() => handleEditBookingClick(item)}
  //           >
  //             Muokkaa
  //           </Button>
  //         </td>
  //       </tr>
  //     ))
  //   ) : (
  //     <div />
  //   );
  // }, [maintainerBookings, maintainerBookingsLoadingStatus]);

  useEffect(() => {}, [ownerBookingsListItems]);
  useEffect(() => {}, [maintainerBookingsListItems]);
  const accountAddressSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.addresses != null ? (
      editedAccount?.addresses.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.postStreetAddress ?? ''}</td>
          <td style={{ width: '100%' }}>{item.postCode ?? ''}</td>
          <td style={{ width: '100%' }}>{item.postCity ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditAddressClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removeAddress(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );

  const accountPhonesSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.phones != null ? (
      editedAccount?.phones.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.phoneNumber ?? ''}</td>
          <td style={{ width: '100%' }}>{item.typeId ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditPhoneClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removePhone(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );
  const handleInputChange = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedAccount, [name]: value };
    setEditedAccount(tempdata);
  };

  const handleInputChangeOmaLiittyma = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedOmaLiittymaSettings, [name]: value };
    setEditedOmaLiittymaSettings(tempdata);
  };

  const handleInputChangeBookingNote = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let tempdata = { ...editedBookingNoteSettings, [name]: value };
    if (name === 'noBookingNote' && value === true) {
      tempdata = {
        ...tempdata,
        bookingNoteBySms: false,
        bookingNoteByEmail: false,
      };
    }
    setEditedBookingNoteSettings(tempdata);
  };

  const handleInputChangeVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedVatSettings, [name]: value };
    setEditedVatSettings(tempdata);
  };
  const handleInputChangeBankSettings = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedBankSettings, [name]: value };
    setEditedBankSettings(tempdata);
  };

  const handleInputChangeOfficerSettings = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedOfficerSettings, [name]: value };
    setEditedOfficerSettings(tempdata);
  };

  const updateOfficerSettingsData = () => {
    const parsed =
      editedOfficerSettings.agentNumber != null
        ? Number(editedOfficerSettings.agentNumber)
        : null;
    const updateData: UpdateOfficerSettingsData = {
      accountId: editedOfficerSettings.accountId,
      agentNumber:
        parsed !== null && !Number.isNaN(parsed) && parsed !== 0
          ? parsed
          : null,
    };
    dispatch(updateOfficerSettingsAsync(updateData)).then(res => {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast(
          res.meta.requestStatus === 'fulfilled'
            ? { Message: 'Virkailijan asetukset on tallennettu.' }
            : {
                IsFailed: true,
                Message: 'Virhe virkailijan asetusten tallennuksessa.',
              },
        ),
      );
    });
  };

  const handleInputChangeTarmo = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedTarmoSettings, [name]: value };
    setEditedTarmoSettings(tempdata);
  };
  const updateOmaLiittmaSettings = event => {
    const updateData = getOmaLiittymaUpdateData();
    dispatch(UpdateOmaLiittymaSettinsAsync(updateData)).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(
          addStatusToast({
            Message: `Tiedot on tallennettu.`,
          }),
        );
      } else {
        dispatch(
          addStatusToast({
            IsFailed: true,
            Message: `Virhe Tietojen tallennuksessa.`,
          }),
        );
      }
    });
    dispatch(
      addStatusToast({
        Message: `Omaliittyma asetukset on tallennettu.`,
      }),
    );
  };
  const updateAccount = event => {
    const updateData = getUpdateData();
    dispatch(updateAccountDataAsync(updateData))
      .then(res => {
        if (res.payload.isSuccess) {
          if (getIsOwner() || getIsCaretaker()) {
            const updateDataOma = getOmaLiittymaUpdateData();
            dispatch(UpdateOmaLiittymaSettinsAsync(updateDataOma));
          }
          dispatch(getAcountDetailsFromDbAsync(params.identityId));
          dispatch(getAccountRolesFromDbAsync(params.identityId));
          dispatch(
            addStatusToast({
              Message: `Tiedot on tallennettu.`,
            }),
          );
        } else {
          dispatch(getAcountDetailsFromDbAsync(params.identityId));
          dispatch(getAccountRolesFromDbAsync(params.identityId));
          dispatch(
            addStatusToast({
              IsFailed: true,
              IsAutohide: false,
              Message: res.payload.message ?? 'Virhe',
            }),
          );
        }
      })
      .catch(err => {
        console.debug('account catch:', err);
      });
  };
  const updateBookingNoteSettings = event => {
    const updateData = getBookingNoteUpdateData();
    console.debug('updateBookingNoteSettings', updateData);
    if (
      updateData &&
      updateData !== null &&
      !updateData.bookingNoteByEmail &&
      !updateData.noBookingNote &&
      !updateData.bookingNoteBySms &&
      !updateData.bookingNoteByMail
    ) {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          Message: `Email, Sms tai ei tiedotetta pitää olla valittuna.`,
          IsFailed: true,
        }),
      );
      return;
    }
    // if (
    //   updateData &&
    //   updateData !== null &&
    //   !updateData.bookingNoteWeekly &&
    //   !updateData.noBookingNote &&
    //   !updateData.bookingNoteImmediatelly
    // ) {
    //   dispatch(
    //     addStatusToast({
    //       Message: `Viikottain tai Heti pitää olla valittuna.`,
    //       IsFailed: true,
    //       IsAutohide: false,
    //     }),
    //   );
    //   return;
    // }
    dispatch(UpdateBookingNoteSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Varaustiedote asetukset on tallennettu.`,
      }),
    );
  };

  const updateVatSettings = event => {
    const updateData = getVatUpdateData();
    dispatch(UpdateVatSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Alv asetukset on tallennettu.`,
      }),
    );
  };

  const updateTarmoSettings = event => {
    const updateData = getTarmoUpdateData();
    dispatch(UpdateTarmoSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Tarmo asetukset on tallennettu.`,
      }),
    );
  };
  const updateBankSettingsData = event => {
    const updateData = getBankSettingsUpdateData();
    const ibaanValid = isValid(editedBankSettings.iban ?? '');
    if (!ibaanValid && editedBankSettings.iban !== '.') {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          IsFailed: true,
          Message: `IBAN-tunnus on virheellinen.`,
        }),
      );
      return;
    }
    const bigValid = isValid(editedBankSettings.bic ?? '');
    if (bigValid && editedBankSettings.bic !== '') {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          IsFailed: true,
          Message: `BIC-TUNNUS on IBAN tunnus.`,
        }),
      );
      return;
    }
    dispatch(UpdateBankSettinsAsync(updateData))
      .then(res => {
        dispatch(clearStatusToasts());
        if (res.payload.ErrorCode != null && res.payload.ErrorCode !== '200') {
          dispatch(
            addStatusToast({
              Message: `Pankkiasetuksen tallennuksessta tapahtui virhe`,
            }),
          );
        } else {
          dispatch(
            addStatusToast({
              Message: `Pankkiasetukset on tallennettu.`,
            }),
          );
        }
      })
      .then(res => {
        dispatch(getBankSettingsAsync(params.identityId));
      });
  };
  const editAddresses = (
    addressesIn: AddressResponse[],
    valuedata: AddressResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          if (valuedata.isPrimary) {
            return { ...valuedata, isPrimary: true };
          }
          return valuedata;
        }
        if (valuedata.isPrimary) {
          return { ...item, isPrimary: false };
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedAddress(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const editEmails = (
    addressesIn: EmailResponse[],
    valuedata: EmailResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          if (valuedata.isPrimary) {
            return { ...valuedata, isPrimary: true };
          }
          return valuedata;
        }
        if (valuedata.isPrimary) {
          return { ...item, isPrimary: false };
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedEmail(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const editPhones = (
    addressesIn: PhoneResponse[],
    valuedata: PhoneResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          if (valuedata.isPrimary) {
            return { ...valuedata, isPrimary: true };
          }
          return valuedata;
        }
        if (valuedata.isPrimary) {
          return { ...item, isPrimary: false };
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedPhone(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const addAddress = () => {
    const newaddress = {
      isPrimary: false,
      postStreetAddress: '',
      additionalAddressLine: '',
      postCity: '',
      countryId: null,
      postCode: '',
      description: '',
      id: 'new',
    } as AddressResponse;
    setEditedAddress(newaddress);
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, newaddress);
    const storeAccount = { ...editedAccount, addresses };
    setEditedAccount(storeAccount);
    setIsAddressModalVisible(true);
  };

  const addEmail = () => {
    const newemail = {
      isPrimary: false,
      isBookingNoteEmail: false,
      emailAddress: '',
      description: '',
      id: 'new',
    } as EmailResponse;
    setEditedEmail(newemail);
    let { emails } = editedAccount;
    emails = editEmails(emails, newemail);
    const customerDetailsStore = { ...editedAccount, emails };
    setEditedAccount(customerDetailsStore);
    setIsEMailModalVisible(true);
  };

  const addPhoneNumber = () => {
    const newPhone = {
      isPrimary: false,
      bookingNoteBySms: false,
      typeId: '',
      phoneNumber: '',
      description: '',
      id: 'new',
    } as PhoneResponse;
    setEditedPhone(newPhone);
    let { phones } = editedAccount;
    phones = editPhones(phones, newPhone);
    const customerDetailsStore = { ...editedAccount, phones };
    setEditedAccount(customerDetailsStore);
    setIsPhoneModalVisible(true);
  };
  const handleInputChangeEmail = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedEmail({
      ...editedEmail,
      [name]: value,
    });
    const editedEmailTemp = {
      ...editedEmail,
      [name]: value,
    };
    let { emails } = editedAccount;
    emails = editEmails(emails, editedEmailTemp);
    const accountDetailsStore = { ...editedAccount, emails };
    setEditedAccount(accountDetailsStore);
  };

  const handleInputChangePhone = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedPhone({
      ...editedPhone,
      [name]: value,
    });
    const editedPhoneTemp = {
      ...editedPhone,
      [name]: value,
    };
    let { phones } = editedAccount;
    phones = editPhones(phones, editedPhoneTemp);
    const accountDetailsStore = { ...editedAccount, phones };
    setEditedAccount(accountDetailsStore);
  };

  const countryOptions = countries.map(country => {
    return { value: country.id, label: country.name };
  });
  const handleInputChangeAddress = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedAddress({
      ...editedAddress,
      [name]: value,
    });
    const editedAddressTemp = {
      ...editedAddress,
      [name]: value,
    };
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, editedAddressTemp);
    const accountDetailsStore = { ...editedAccount, addresses };
    setEditedAccount(accountDetailsStore);
  };
  const handleChangesellVat = event => {
    if (editedVatSettings != null) {
      const storedSellVatData: VatSettingsResponse = {
        ...editedVatSettings,
        purchaseVatId: event.value,
      };
      setEditedVatSettings(storedSellVatData);
    }
  };
  const handleDeleteUser = event => {
    const accountUpdateData = {
      id: editedAccount.id,
      contactUpdateData: null,
      accountsRoles: null,
    } as AccountUpdateData;
    dispatch(markAccountDeletedAsyncAsync(accountUpdateData)).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(
          addStatusToast({
            Message: `Käyttäjä on poistettu.`,
          }),
        );
        history(
          `/users/search?PageNumber=${PageNumber}&PageSize=${PageSize}&searchString=${searchString}&culture=${culture}&roleIds=${roleIds}&isActive=${isActive}&paysVat=${paysVat}&createdFrom=${createdFrom}&createdTo=${createdTo}&updatedFrom=${updatedFrom}&updatedTo=${updatedTo}&companyId=${companyId}&roleFilter=${roleFilter}`,
        );
      } else if (res.meta.requestStatus === 'rejected') {
        dispatch(
          addStatusToast({
            IsFailed: true,
            Message: `Käyttäjän poistamisessa tapahtui virhe.`,
          }),
        );
      }
    });
  };
  const handleSelectChange = (selectedOption, name) => {
    const newaddressData = { ...editedAddress };
    newaddressData[name] = selectedOption.value;
    setEditedAddress(newaddressData);
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, newaddressData);
    const accountDetailsStore = { ...editedAccount, addresses };
    setEditedAccount(accountDetailsStore);
  };

  const handleSelectChangeBankType = (selectedOption, name) => {
    const newbankData = { ...editedBankSettings };
    newbankData[name] = selectedOption.value;
    setEditedBankSettings(newbankData);
    const { additionalInformation } = editedAccount;
  };

  const accountEmailsSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.emails != null ? (
      editedAccount?.emails.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.emailAddress ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditEmailClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removeEmail(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );

  return (
    <>
      <Helmet>
        <title>Käyttäjätiedot</title>
      </Helmet>
      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Käyttäjät']}
          Title={`${
            accountLoadingStatus === LOADING_STATUS.IDLE
              ? `${getIsOfficer() && editedOfficerSettings && editedOfficerSettings.officerNumber !== '0' && editedOfficerSettings.officerNumber !== undefined ? editedOfficerSettings.officerNumber : ''}
                 ${getIsCaretaker() && editedOmaLiittymaSettings && editedOmaLiittymaSettings.caretakerNumber !== null && editedOmaLiittymaSettings.caretakerNumber !== undefined && editedOmaLiittymaSettings.caretakerNumber !== 0 ? `Huoltajanumero: ${editedOmaLiittymaSettings.caretakerNumber}` : ''}
                 ${getIsOwner() && editedOmaLiittymaSettings && editedOmaLiittymaSettings.ownerNumber !== null && editedOmaLiittymaSettings.ownerNumber !== undefined && editedOmaLiittymaSettings.ownerNumber !== 0 ? `Omistajanumero: ${editedOmaLiittymaSettings.ownerNumber}` : ''} ${editedAccount.firstName} ${editedAccount.lastName}`
              : ''
          }`}
        />

        {accountLoadingStatus === LOADING_STATUS.LOADING && (
          <Loader IsLoading />
        )}
        {accountLoadingStatus === LOADING_STATUS.IDLE && (
          <VerticalTabs
            ActiveTab={activeTab}
            ChangeTab={setActiveTab}
            TabContainerId="customerTabs"
            Tabs={Tabs}
          >
            <VerticalTabPane IsActive={activeTab === Tabs[0]} Name={Tabs[0]}>
              <div>
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChange}
                    Id="firstName"
                    Name="firstName"
                    Value={editedAccount.firstName ?? ''}
                    IsRequired={false}
                    Label="Etunimi"
                  />
                </div>
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChange}
                    Id="lastName"
                    Name="lastName"
                    Value={editedAccount.lastName ?? ''}
                    IsRequired={false}
                    Label="Sukunimi / Yritys"
                  />
                </div>
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChange}
                    Id="companyName"
                    Name="companyName"
                    Value={editedAccount.companyName ?? ''}
                    IsRequired={false}
                    Label="Yhteyshenkilö"
                  />
                </div>
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChange}
                    Id="description"
                    IsTextArea
                    Name="description"
                    Value={editedAccount.description ?? ''}
                    IsRequired={false}
                    Label="Kuvaus"
                  />
                </div>
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChange}
                    Id="additionalInformation"
                    IsTextArea
                    Name="additionalInformation"
                    Value={editedAccount.additionalInformation ?? ''}
                    IsRequired={false}
                    Label="Lisätiedot"
                  />
                </div>
              </div>
              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
                HandleDelete={handleDeleteUser}
              />
            </VerticalTabPane>

            <VerticalTabPane IsActive={activeTab === Tabs[1]} Name={Tabs[1]}>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">Osoitteet</h2>
                <Button size="sm" onClick={addAddress}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountAddressSelectOptions}</tbody>
                </Table>
              </div>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">
                  Puhelinnumerot
                </h2>
                <Button size="sm" onClick={addPhoneNumber}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountPhonesSelectOptions}</tbody>
                </Table>
              </div>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">
                  Sähköposti-osoitteet
                </h2>
                <Button size="sm" onClick={addEmail}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountEmailsSelectOptions}</tbody>
                </Table>
              </div>
              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
              />
            </VerticalTabPane>

            <VerticalTabPane IsActive={activeTab === Tabs[2]} Name={Tabs[2]}>
              {rolesStatus === LOADING_STATUS.IDLE && (
                <>
                  <div>{rolesSelectOptions}</div>
                  {travelAgencyDomainSelectionVisible && (
                    <>
                      <Form.Label
                        htmlFor="TravelAgencyDomain"
                        className="form-label"
                      >
                        Käyttäjän matkanjärjestäjä toimialueet
                      </Form.Label>
                      <Select
                        isMulti
                        inputId="TravelAgencyDomain"
                        name="TravelAgencyDomain"
                        placeholder="Valitse toimialue"
                        isClearable
                        options={travelAgencyDomainsList}
                        classNames={{
                          control: state =>
                            'form-control form-control-react-select mb-24',
                        }}
                        onChange={option =>
                          handleTravelAgencyDomainsChange(option)
                        }
                        value={travelAgencyDomainsList.filter(option =>
                          editedTravelAgencyDomains.includes(option.value),
                        )}
                      />
                    </>
                  )}
                </>
              )}
              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
              />
            </VerticalTabPane>
            <VerticalTabPane IsActive={activeTab === Tabs[3]} Name={Tabs[3]}>
              {rolesStatus === LOADING_STATUS.IDLE &&
                (getIsOwner() || getIsOfficer() || getIsCaretaker()) &&
                activeTab === Tabs[3] && (
                  <AccountSitesTabContainer
                    backLinkUrl={backLinkUrl}
                    accountId={params.identityId}
                  />
                )}
            </VerticalTabPane>
            {rolesStatus === LOADING_STATUS.IDLE &&
              (getIsOwner() || getIsCaretaker()) && (
                <VerticalTabPane
                  IsActive={activeTab === Tabs[4]}
                  Name={Tabs[4]}
                >
                  <div>
                    {getIsOwner() && (
                      <div className="mb-24">
                        <InputNumber
                          HandleChange={handleInputChangeOmaLiittyma}
                          Id="ownerNumber"
                          Name="ownerNumber"
                          Value={editedOmaLiittymaSettings.ownerNumber ?? ''}
                          IsRequired={false}
                          Label="Omistaja numero"
                          IsReadOnly
                        />
                      </div>
                    )}
                    {getIsCaretaker() && (
                      <div className="mb-24">
                        <InputNumber
                          HandleChange={handleInputChangeOmaLiittyma}
                          Id="caretakerNumber"
                          Name="caretakerNumber"
                          Value={
                            editedOmaLiittymaSettings.caretakerNumber ?? ''
                          }
                          IsRequired={false}
                          Label="Huoltaja numero"
                          IsReadOnly
                        />
                      </div>
                    )}
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeOmaLiittyma}
                        Id="hideBillOwnerData"
                        Name="hideBillOwnerData"
                        Checked={editedOmaLiittymaSettings.hideBillOwnerData}
                        IsRequired={false}
                        Label="Piilota laskulta"
                      />
                    </div>
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeOmaLiittyma}
                        Id="directLinkPath"
                        Name="directLinkPath"
                        Value={editedOmaLiittymaSettings.directLinkPath ?? ''}
                        IsRequired={false}
                        Label="Suoralinkki"
                      />
                    </div>
                  </div>
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={event => updateOmaLiittmaSettings(event)}
                  />
                </VerticalTabPane>
              )}
            {rolesStatus === LOADING_STATUS.IDLE &&
              (getIsOwner() || getIsCaretaker()) && (
                <VerticalTabPane
                  IsActive={activeTab === Tabs[5]}
                  Name={Tabs[5]}
                >
                  <div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="noBookingNote"
                        Name="noBookingNote"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.noBookingNote) ??
                          false
                        }
                        IsRequired={false}
                        Label="Ei varaustiedotetta"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="bookingNoteBySms"
                        Name="bookingNoteBySms"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.bookingNoteBySms) ??
                          false
                        }
                        IsRequired={false}
                        Label="Varaustiedote Sms viestinä"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="bookingNoteByEmail"
                        Name="bookingNoteByEmail"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.bookingNoteByEmail) ??
                          false
                        }
                        IsRequired={false}
                        Label="Varaustiedote sähköpostilla"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="bookingNoteByMail"
                        Name="bookingNoteByMail"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.bookingNoteByMail) ??
                          false
                        }
                        IsRequired={false}
                        Label="Varaustiedote postin kautta"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="bookingNoteWeekly"
                        Name="bookingNoteWeekly"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.bookingNoteWeekly) ??
                          false
                        }
                        IsRequired={false}
                        Label="Varaustiedotteet viikoittain"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeBookingNote}
                        Id="bookingNoteImmediatelly"
                        Name="bookingNoteImmediatelly"
                        Checked={
                          (editedBookingNoteSettings &&
                            editedBookingNoteSettings.bookingNoteImmediatelly) ??
                          false
                        }
                        IsRequired={false}
                        Label="Varaustiedote heti"
                      />
                    </div>
                  </div>
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={event => updateBookingNoteSettings(event)}
                  />
                </VerticalTabPane>
              )}
            {rolesStatus === LOADING_STATUS.IDLE &&
              (getIsOwner() || getIsCaretaker()) && (
                <VerticalTabPane
                  IsActive={activeTab === Tabs[6]}
                  Name={Tabs[6]}
                >
                  <div>
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeBankSettings}
                        Id="bic"
                        Name="bic"
                        Value={
                          (editedBankSettings && editedBankSettings.bic) ?? ''
                        }
                        IsRequired={false}
                        Label="BIC"
                      />
                    </div>
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeBankSettings}
                        Id="iban"
                        Name="iban"
                        Value={
                          (editedBankSettings && editedBankSettings.iban) ?? ''
                        }
                        IsRequired={false}
                        Label="IBAN"
                      />
                    </div>
                  </div>
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={event => updateBankSettingsData(event)}
                  />
                </VerticalTabPane>
              )}
            {getIsOfficer() && editedOfficerSettings != null && (
              <VerticalTabPane IsActive={activeTab === Tabs[7]} Name={Tabs[7]}>
                <div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeOfficerSettings}
                      Id="officerNumber"
                      Name="officerNumber"
                      Value={
                        editedOfficerSettings.officerNumber != null
                          ? editedOfficerSettings.officerNumber
                          : ''
                      }
                      IsRequired={false}
                      IsReadOnly
                      Label="Virkailija numero"
                    />
                  </div>
                  <div className="mb-24">
                    <InputNumber
                      HandleChange={handleInputChangeOfficerSettings}
                      Id="agentNumber"
                      Name="agentNumber"
                      Value={
                        editedOfficerSettings.agentNumber != null
                          ? editedOfficerSettings.agentNumber
                          : ''
                      }
                      IsRequired={false}
                      IsReadOnly={!hasSettingModify}
                      Label="Agenttinumero"
                    />
                  </div>
                </div>
                {hasSettingModify && (
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={() => updateOfficerSettingsData()}
                  />
                )}
              </VerticalTabPane>
            )}
            {(getIsOwner() || getIsCaretaker()) &&
              editedVatSettings != null &&
              vatSettingsLoadingStatus === LOADING_STATUS.IDLE && (
                <VerticalTabPane
                  IsActive={activeTab === Tabs[8]}
                  Name={Tabs[8]}
                >
                  <div>
                    {vatSettingsLoadingStatus !== LOADING_STATUS.IDLE && (
                      <Spinner />
                    )}
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeVat}
                        Id="yCode"
                        Name="yCode"
                        Value={
                          editedVatSettings.yCode != null
                            ? editedVatSettings.yCode
                            : ''
                        }
                        IsRequired={false}
                        Label="Y-tunnus"
                      />
                    </div>
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeVat}
                        Id="hetu"
                        Name="hetu"
                        Value={
                          editedVatSettings.hetu != null
                            ? editedVatSettings.hetu
                            : ''
                        }
                        IsRequired={false}
                        Label="Hetu"
                      />
                    </div>
                    <div className="mb-24">
                      <InputText
                        HandleChange={handleInputChangeVat}
                        Id="otherVatCode"
                        Name="otherVatCode"
                        Value={
                          editedVatSettings.otherVatCode != null
                            ? editedVatSettings.otherVatCode
                            : ''
                        }
                        IsRequired={false}
                        Label="Muu Alv tunnus"
                      />
                    </div>
                    <div className="mb-24">
                      <InputCheck
                        HandleChange={handleInputChangeVat}
                        Id="paysVat"
                        Name="paysVat"
                        Checked={editedVatSettings.paysVat}
                        IsRequired={false}
                        Label="Alv velvollinen"
                      />
                    </div>
                    <ActionButtons
                      CancelButtonUrl={backLinkUrl}
                      HandleSave={event => updateVatSettings(event)}
                    />
                  </div>
                </VerticalTabPane>
              )}
            {(getIsOwner() === true || getIsCaretaker() === true) && (
              <VerticalTabPane IsActive={activeTab === Tabs[9]} Name={Tabs[9]}>
                <>
                  <DataTable
                    ColumnWidths={[200, null]}
                    Header={['Mökin numero', 'Mökin nimi']}
                    Items={accommodationTableItems}
                    LegendText="majoitusta"
                    TotalItems={accommodationsPagination.totalCount}
                    OpenInNewWindow
                  />
                  <PaginationComponent
                    OnPageClick={HandlePaginationClick}
                    PaginationData={accommodationsPagination}
                  />
                </>
                {/* )} */}
              </VerticalTabPane>
            )}
            {rolesStatus === LOADING_STATUS.IDLE && getIsCaretaker() && (
              <VerticalTabPane
                IsActive={activeTab === Tabs[10]}
                Name={Tabs[10]}
              >
                <div className="mb-24">
                  <InputText
                    HandleChange={handleInputChangeTarmo}
                    Id="tarmoAPIAddress"
                    Name="tarmoAPIAddress"
                    Value={
                      editedTarmoSettings.tarmoAPIAddress != null
                        ? editedTarmoSettings.tarmoAPIAddress
                        : ''
                    }
                    IsRequired={false}
                    Label="Tarmo API-osoite"
                  />
                  <InputText
                    HandleChange={handleInputChangeTarmo}
                    Id="tarmoAPIKey"
                    Name="tarmoAPIKey"
                    Value={
                      editedTarmoSettings.tarmoAPIKey != null
                        ? editedTarmoSettings.tarmoAPIKey
                        : ''
                    }
                    IsRequired={false}
                    Label="Tarmo API-avain"
                  />
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={event => updateTarmoSettings(event)}
                  />
                </div>
              </VerticalTabPane>
            )}
            {getIsOwner() === true && (
              <VerticalTabPane
                IsActive={activeTab === Tabs[11]}
                Name={Tabs[11]}
              >
                {ownerBookingsLoadingStatus === LOADING_STATUS.IDLE &&
                  ownerBookingsListItems != null && (
                    <div>
                      <Table>
                        <thead>
                          <tr>
                            <th>Nimi</th>
                            <th>Aika</th>
                            <th>Asiakas</th>
                            <th>Kohde</th>
                            <th>Tila</th>
                            <th>Hinta</th>
                            <th>Ostohinta</th>
                          </tr>
                        </thead>
                        <tbody>{ownerBookingsListItems}</tbody>
                      </Table>
                      <PaginationComponent
                        OnPageClick={handlePaginationClickOwners}
                        PaginationData={ownerBookingsPagination}
                      />
                    </div>
                  )}
              </VerticalTabPane>
            )}
            <VerticalTabPane IsActive={activeTab === Tabs[12]} Name={Tabs[12]}>
              <PersonalIdentityTab
                accountId={params.identityId || ''}
                isActive={activeTab === Tabs[12]}
              />
            </VerticalTabPane>
            {getIsCaretaker() === true && (
              <VerticalTabPane
                IsActive={activeTab === Tabs[11]}
                Name={Tabs[11]}
              >
                {maintainerBookingsLoadingStatus === LOADING_STATUS.IDLE &&
                  maintainerBookingsListItems != null && (
                    <div>
                      <Table>
                        <thead>
                          <tr>
                            <th>Nimi</th>
                            <th>Aika</th>
                            <th>Asiakas</th>
                            <th>Kohde</th>
                            <th>Tila</th>
                            <th>Hinta</th>
                            <th>Ostohinta</th>
                          </tr>
                        </thead>
                        <tbody>{maintainerBookingsListItems}</tbody>
                      </Table>
                      <PaginationComponent
                        OnPageClick={handlePaginationClickMaintainers}
                        PaginationData={maintainerBookingsPagination}
                      />
                    </div>
                  )}
              </VerticalTabPane>
            )}
          </VerticalTabs>
        )}
        <Modal show={isAddressModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa osoitetta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-24">
              <Form.Label>Katuosoite</Form.Label>
              <Form.Control
                name="postStreetAddress"
                value={editedAddress.postStreetAddress ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Osoitteen lisärivi</Form.Label>
              <Form.Control
                name="additionalAddressLine"
                value={editedAddress.additionalAddressLine ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Postinumero</Form.Label>
              <Form.Control
                name="postCode"
                value={editedAddress.postCode ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Postitoimipaikka</Form.Label>
              <Form.Control
                name="postCity"
                value={editedAddress.postCity ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Maa</Form.Label>
              <Select
                inputId="countryId"
                name="countryId"
                placeholder="Valitse maa"
                isClearable
                isSearchable
                options={countryOptions}
                onChange={option => handleSelectChange(option, 'countryId')}
                value={countryOptions.filter(
                  option => option.value === editedAddress.countryId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <Form.Check
              className="my-24"
              label="Oletusosoite ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangeAddress}
              inline
              checked={editedAddress.isPrimary}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsAddressModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isPhoneModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa puhelinnumeroa</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-24">
              <Form.Label>Puhelinnumero</Form.Label>
              <Form.Control
                name="phoneNumber"
                value={editedPhone.phoneNumber ?? ''}
                onChange={handleInputChangePhone}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Kuvaus</Form.Label>
              <Form.Control
                name="description"
                value={editedPhone.description ?? ''}
                onChange={handleInputChangePhone}
              />
            </Form.Group>
            <Form.Check
              className="my-24"
              label="Oletusnumero ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangePhone}
              inline
              checked={editedPhone.isPrimary}
            />
            <Form.Check
              className="my-24"
              label="Varaustiedote sms numero ?"
              id="bookingNoteBySms"
              name="bookingNoteBySms"
              onChange={handleInputChangePhone}
              inline
              checked={editedPhone.bookingNoteBySms}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsPhoneModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isEMailModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa sähköpostiosoitetta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-24">
              <Form.Label>Sähköposti</Form.Label>
              <Form.Control
                name="emailAddress"
                value={editedEmail.emailAddress ?? ''}
                onChange={handleInputChangeEmail}
              />
            </Form.Group>
            <Form.Group className="mb-24">
              <Form.Label>Kuvaus</Form.Label>
              <Form.Control
                name="description"
                value={editedEmail.description ?? ''}
                onChange={handleInputChangeEmail}
              />
            </Form.Group>

            <Form.Check
              className="my-24"
              label="Oletus sähköposti ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangeEmail}
              inline
              checked={editedEmail.isPrimary}
            />
            <Form.Check
              className="my-24"
              label="Varaustiedote sähköposti ?"
              id="isBookingNoteEmail"
              name="isBookingNoteEmail"
              onChange={handleInputChangeEmail}
              inline
              checked={editedEmail.isBookingNoteEmail}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsEMailModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default IdentityUserDetails;
